/**
 * Burn the image viewer's sidebar edits (brightness / contrast / saturation / grayscale / sharpness) into a file.
 *
 * On screen the edits are only a CSS filter, so anything that leaves the viewer — a download, a print, a share — has to be
 * redrawn through the same maths or it comes out untouched.
 *
 * Hand it the picture's BYTES (a Blob from the backend proxy, or from a local blob: URL) whenever you can: a Blob is
 * same-origin, so the canvas can never be tainted. A bare URL is also accepted, but the viewer has already shown it in a
 * plain <img>, which caches it WITHOUT CORS headers — loading that cached copy again with crossOrigin="anonymous" fails
 * (Chrome) even when the server allows CORS, and the edits were silently dropped. A URL is therefore re-fetched in CORS mode
 * with `cache: 'reload'` (same fix as bgPresets / the manual editor).
 *
 * Throws when the picture cannot be read or redrawn — callers must tell the user, never quietly hand over the unedited file.
 */

const FILTER_PROBE = { value: null };

// Safari (before 18) ignores CanvasRenderingContext2D.filter: it would draw the picture UNFILTERED. Probe once, with a real draw.
function canvasFilterWorks() {
    if (FILTER_PROBE.value !== null) return FILTER_PROBE.value;
    try {
        const c = document.createElement('canvas');
        c.width = 1; c.height = 1;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(0, 0, 1, 1);
        const probe = document.createElement('canvas');
        probe.width = 1; probe.height = 1;
        const pctx = probe.getContext('2d');
        pctx.filter = 'grayscale(100%)';
        pctx.drawImage(c, 0, 0);
        const [r, g] = pctx.getImageData(0, 0, 1, 1).data;
        FILTER_PROBE.value = Math.abs(r - g) < 8; // red drawn through grayscale(100%) must come out grey
    } catch {
        FILTER_PROBE.value = false;
    }
    return FILTER_PROBE.value;
}

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

// The CSS filter functions, applied in order (brightness → contrast → saturate → grayscale), each result clamped — the same
// maths as the filter string below, for browsers whose canvas has no `filter`.
function applyFilterByHand(imageData, { brightness, contrast, saturation, grayscale }) {
    const d = imageData.data;
    const b = brightness / 100;
    const k = contrast / 100;
    const s = saturation / 100;
    const inv = 1 - Math.min(1, Math.max(0, grayscale / 100));
    const sat = [
        0.213 + 0.787 * s, 0.715 - 0.715 * s, 0.072 - 0.072 * s,
        0.213 - 0.213 * s, 0.715 + 0.285 * s, 0.072 - 0.072 * s,
        0.213 - 0.213 * s, 0.715 - 0.715 * s, 0.072 + 0.928 * s,
    ];
    const gray = [
        0.2126 + 0.7874 * inv, 0.7152 - 0.7152 * inv, 0.0722 - 0.0722 * inv,
        0.2126 - 0.2126 * inv, 0.7152 + 0.2848 * inv, 0.0722 - 0.0722 * inv,
        0.2126 - 0.2126 * inv, 0.7152 - 0.7152 * inv, 0.0722 + 0.9278 * inv,
    ];
    for (let i = 0; i < d.length; i += 4) {
        let r = clamp01((d[i] / 255) * b);
        let g = clamp01((d[i + 1] / 255) * b);
        let bl = clamp01((d[i + 2] / 255) * b);
        r = clamp01((r - 0.5) * k + 0.5); g = clamp01((g - 0.5) * k + 0.5); bl = clamp01((bl - 0.5) * k + 0.5);
        const r2 = clamp01(sat[0] * r + sat[1] * g + sat[2] * bl);
        const g2 = clamp01(sat[3] * r + sat[4] * g + sat[5] * bl);
        const b2 = clamp01(sat[6] * r + sat[7] * g + sat[8] * bl);
        d[i] = clamp01(gray[0] * r2 + gray[1] * g2 + gray[2] * b2) * 255;
        d[i + 1] = clamp01(gray[3] * r2 + gray[4] * g2 + gray[5] * b2) * 255;
        d[i + 2] = clamp01(gray[6] * r2 + gray[7] * g2 + gray[8] * b2) * 255;
    }
}

// The viewer's sharpen filter: a 3×3 unsharp kernel (0 −s 0 / −s 1+4s −s / 0 −s 0), edges and alpha left alone.
function sharpen(ctx, w, h, amount) {
    const s = amount / 10;
    const imageData = ctx.getImageData(0, 0, w, h);
    const src = new Uint8ClampedArray(imageData.data);
    const dst = imageData.data;
    for (let y = 1; y < h - 1; y += 1) {
        for (let x = 1; x < w - 1; x += 1) {
            const i = (y * w + x) * 4;
            for (let c = 0; c < 3; c += 1) {
                dst[i + c] = (1 + 4 * s) * src[i + c]
                    - s * (src[((y - 1) * w + x) * 4 + c] + src[(y * w + (x - 1)) * 4 + c]
                        + src[(y * w + (x + 1)) * 4 + c] + src[((y + 1) * w + x) * 4 + c]);
            }
        }
    }
    ctx.putImageData(imageData, 0, 0);
}

/** Bytes of an image URL, fetched fresh in CORS mode (see the file header for why `cache: 'reload'`). */
export async function fetchImageBlob(url) {
    const resp = await fetch(url, { mode: 'cors', cache: 'reload' });
    if (!resp.ok) throw new Error(`image fetch failed: ${resp.status}`);
    return resp.blob();
}

/**
 * @param {Blob|string} source   the picture: a Blob (preferred) or a URL
 * @param {{brightness?:number, contrast?:number, saturation?:number, grayscale?:number, sharp?:number}} adjustments
 *        the sidebar values: percentages (100 = unchanged; grayscale 0 = unchanged) and sharpness 0–10
 * @param {{type?:string}} [options]  output type; default PNG for sources that can carry transparency, else JPEG
 * @returns {Promise<Blob>}
 */
export async function bakeImageAdjustments(source, adjustments = {}, { type } = {}) {
    const { brightness = 100, contrast = 100, saturation = 100, grayscale = 0, sharp = 0 } = adjustments;
    const blob = source instanceof Blob ? source : await fetchImageBlob(source);

    const objectUrl = URL.createObjectURL(blob);
    const canvas = document.createElement('canvas');
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new window.Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('image decode failed'));
            el.src = objectUrl;
        });
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (canvasFilterWorks()) {
            ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) grayscale(${grayscale}%)`;
            ctx.drawImage(img, 0, 0);
        } else {
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            applyFilterByHand(imageData, { brightness, contrast, saturation, grayscale });
            ctx.putImageData(imageData, 0, 0);
        }
        if (sharp > 0) sharpen(ctx, canvas.width, canvas.height, sharp);

        const outType = type || (/png|webp|gif|avif|svg/.test(blob.type) ? 'image/png' : 'image/jpeg');
        return await new Promise((resolve, reject) => {
            canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('image export failed'))), outType, 0.95);
        });
    } finally {
        URL.revokeObjectURL(objectUrl);
        canvas.width = 0;
        canvas.height = 0;
    }
}
